import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  UploaderOptions,
  UploadFile,
  UploadInput,
  UploadOutput,
} from 'ngx-uploader';
import { AuthService } from 'src/app/core/services/auth.service';
import { MediaType } from 'src/app/core/types/types';
import { TranslateService } from '@ngx-translate/core';
import { getToken, UserState } from 'src/app/login/state/user.reducers';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

@Component({
  selector: 'cm-media-library-upload',
  templateUrl: './media-library-upload.component.html',
  styleUrls: ['./media-library-upload.component.scss'],
})
export class MediaLibraryUploadComponent implements OnInit {
  options: UploaderOptions;
  formData: FormData;
  files: UploadFile[];
  uploadInput: EventEmitter<UploadInput>;
  humanizeBytes: Function;
  dragOver: boolean;
  visibility: string = 'private';
  statusMessage: string = '';

  private translateStrings: any;
  private maxImageFileSize = 50000000;
  private maxVideoFileSize = 300000000;

  uploadOptions = {
    private: 'true',
    owner: null,
  };
  allowedImageExtensions = ['image/jpg', 'image/png', 'image/jpeg', 'image/heic', 'image/heif'];
  allowedVideoExtensions = [
    'video/mp4',
    'video/avi',
    'video/mkv',
    'video/x-m4v',
    'video/flv',
    'video/quicktime',
    'video/mov',
  ];

  @Input() mediaType: string;
  @Output() filesAdded: EventEmitter<UploadOutput> = new EventEmitter();
  @Output() progress: EventEmitter<UploadOutput> = new EventEmitter();
  @Output() complete: EventEmitter<UploadOutput> = new EventEmitter();

  constructor(
    private authService: AuthService,
    private translate: TranslateService,
    private store: Store<UserState>
  ) {
    this.uploadInput = new EventEmitter<UploadInput>();
    this.files = [];

    const tokens = ['COMPONENTS.MEDIA_LIBRARY.UPLOAD.ERROR_MESSAGE'];
    this.translate
      .get(tokens)
      .subscribe((strings) => (this.translateStrings = strings));
  }

  ngOnInit(): void {
    const isPhotos = this.mediaType === MediaType.PHOTOS;
    this.options = {
      concurrency: isPhotos ? 5 : 1,
      maxFileSize: isPhotos ? this.maxImageFileSize : this.maxVideoFileSize,
      allowedContentTypes: isPhotos ? [...this.allowedImageExtensions] : [...this.allowedVideoExtensions],
    };
  }

  buildUrl() {
    let uploadUrl = '';
    if (this.mediaType === MediaType.PHOTOS) {
      uploadUrl = environment.apiUrl + '/message-image';
    } else {
      uploadUrl = environment.apiUrl + '/message-video';
    }
    uploadUrl += this.uploadOptions.owner
      ? '?owner=' + this.uploadOptions.owner.id
      : '';
    return uploadUrl;
  }

  onUploadOutput(output: UploadOutput): void {
    let token: string;
    this.store
      .select(getToken)
      .pipe(take(1))
      .subscribe((t) => (token = t));

    switch (output.type) {
      case 'allAddedToQueue':
        this.statusMessage = '';
        const event: UploadInput = {
          type: 'uploadAll',
          url: this.buildUrl(),
          fieldName: this.mediaType === MediaType.PHOTOS ? 'image' : 'video',
          method: 'POST',
          data: { caption: 'upload', is_private: this.uploadOptions.private },
          headers: { Authorization: `Bearer ${token}` },
        };
        this.uploadInput.emit(event);
        break;
      case 'addedToQueue':
        this.statusMessage = '';
        if (typeof output.file !== 'undefined') {
          this.files.push(output.file);
        }
        this.filesAdded.emit(output);
        break;
      case 'uploading':
        if (typeof output.file !== 'undefined') {
          // update current data in files array for uploading file
          const index = this.files.findIndex(
            (file) =>
              typeof output.file !== 'undefined' && file.id === output.file.id
          );
          this.files[index] = output.file;
          //this.progress = output.file.progress.data.percentage / 100;
          this.progress.emit(output);
        }
        break;
      case 'removed':
        // remove file from array when removed
        this.files = this.files.filter(
          (file: UploadFile) => file !== output.file
        );
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
      case 'rejected':
        const file = output.file;
        const fileName = file?.name || 'unknown';
        const fileType = file?.type || 'no type detected';
        const fileSize = file?.size ? (file.size / 1048576).toFixed(2) + 'MB' : 'unknown size';
        const maxSize = (this.mediaType === MediaType.PHOTOS ? this.maxImageFileSize : this.maxVideoFileSize) / 1048576;
        console.warn(
          `[MediaLibrary] File rejected: "${fileName}" | type: "${fileType}" | size: ${fileSize} | ` +
          `maxSize: ${maxSize}MB | allowedTypes: [${this.mediaType === MediaType.PHOTOS ? this.allowedImageExtensions.join(', ') : this.allowedVideoExtensions.join(', ')}]`
        );
        this.statusMessage =
          this.translateStrings[
            'COMPONENTS.MEDIA_LIBRARY.UPLOAD.ERROR_MESSAGE'
          ];
        this.uploadInput.emit({ type: 'removeAll' });
        this.files = [];
        break;
      case 'done':
        console.info('upload done!');
        this.files = [];
        this.complete.emit(output);
        break;
    }
  }

  onVisibilityChanged() {
    if (this.visibility === 'private') {
      this.uploadOptions.private = 'true';
    } else {
      this.uploadOptions.private = 'false';
    }
  }
}
